import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/common/DashboardLayout';
import StatusBadge from '../components/common/StatusBadge';
import Pagination from '../components/common/Pagination';
import { individualAPI, adoptionAPI } from '../services/api';
import MonitoringMapCard from '../components/common/MonitoringMapCard';
import SpeciesReport from './SpeciesReport';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js';
import { Pie, Doughnut, Bar } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

// ---------------------------------------------------------------------------
// Nav configuration
// ---------------------------------------------------------------------------
const NAV_ITEMS = [
  { path: '', label: 'Dashboard', icon: '\uD83D\uDCCA' },
  { section: 'Trees' },
  { path: '/sponsorships', label: 'My Sponsorships', icon: '\uD83C\uDF33' },
  { path: '/adopt', label: 'Adopt a Site', icon: '\uD83C\uDFDE\uFE0F' },
  { path: '/my-adoptions', label: 'My Adoptions', icon: '\uD83C\uDF3F' },
  { path: '/species-report', label: 'Species Report', icon: '\uD83C\uDF3B' },
  { section: 'Community' },
  { path: '/volunteering', label: 'Volunteering', icon: '\uD83E\uDD1D' },
  { path: '/ideas', label: 'Collaboration Ideas', icon: '\uD83D\uDCA1' },
  { path: '/blog', label: 'My Blog Posts', icon: '\u270D\uFE0F' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatCurrency(amount) {
  if (amount == null) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
}

// ---------------------------------------------------------------------------
// Reusable Modal wrapper
// ---------------------------------------------------------------------------
function Modal({ open, title, onClose, children }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chart color palette (warm green / earth tones)
// ---------------------------------------------------------------------------
const COLORS = {
  green1: '#2E7D32',
  green2: '#4CAF50',
  green3: '#81C784',
  green4: '#C8E6C9',
  amber: '#FF8F00',
  orange: '#EF6C00',
  brown: '#5D4037',
  earth1: '#8D6E63',
  earth2: '#A1887F',
  earth3: '#D7CCC8',
  red: '#C62828',
  blue: '#1565C0',
  teal: '#00796B',
};

const chartPalette = [
  COLORS.green1, COLORS.amber, COLORS.green3, COLORS.earth1,
  COLORS.teal, COLORS.orange, COLORS.brown, COLORS.blue,
  COLORS.green4, COLORS.earth2, COLORS.red, COLORS.earth3,
];

// ---------------------------------------------------------------------------
// Overview sub-page (chart-rich dashboard)
// ---------------------------------------------------------------------------
function Overview() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      try {
        const res = await individualAPI.getDashboard();
        setDashboard(res.data);
      } catch (err) {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  if (!dashboard) {
    return <div className="empty-state">Unable to load dashboard.</div>;
  }

  const {
    total_sponsored = 0,
    total_spent = 0,
    plantation_status = {},
    tree_types = {},
    total_signups = 0,
    total_ideas = 0,
    idea_status = {},
    payment_status = {},
    total_trees_monitored = 0,
    monitoring_avg_survival = 0,
    monitoring_gps = [],
  } = dashboard;

  // ---------- Chart Data ----------

  // 1. Plantation Progress (Doughnut)
  const plantationLabels = Object.keys(plantation_status).map(
    (k) => k.charAt(0).toUpperCase() + k.slice(1)
  );
  const plantationValues = Object.values(plantation_status);
  const plantationColors = [COLORS.amber, COLORS.green2, COLORS.green1, COLORS.teal];
  const plantationData = {
    labels: plantationLabels,
    datasets: [
      {
        data: plantationValues,
        backgroundColor: plantationColors.slice(0, plantationLabels.length),
        borderWidth: 2,
        borderColor: '#fff',
        hoverOffset: 6,
      },
    ],
  };

  // 2. Tree Species (Pie)
  const treeLabels = Object.keys(tree_types);
  const treeValues = Object.values(tree_types);
  const treeData = {
    labels: treeLabels,
    datasets: [
      {
        data: treeValues,
        backgroundColor: chartPalette.slice(0, treeLabels.length),
        borderWidth: 2,
        borderColor: '#fff',
        hoverOffset: 6,
      },
    ],
  };

  // 3. Idea Status (Doughnut)
  const ideaLabels = Object.keys(idea_status).map(
    (k) => k.charAt(0).toUpperCase() + k.slice(1)
  );
  const ideaValues = Object.values(idea_status);
  const ideaColors = [COLORS.amber, COLORS.green2, COLORS.blue];
  const ideaData = {
    labels: ideaLabels,
    datasets: [
      {
        data: ideaValues,
        backgroundColor: ideaColors.slice(0, ideaLabels.length),
        borderWidth: 2,
        borderColor: '#fff',
        hoverOffset: 6,
      },
    ],
  };

  // 4. Payment Status (Bar)
  const paymentLabels = Object.keys(payment_status).map(
    (k) => k.charAt(0).toUpperCase() + k.slice(1)
  );
  const paymentValues = Object.values(payment_status);
  const paymentBarColors = paymentLabels.map((label) => {
    const l = label.toLowerCase();
    if (l === 'completed') return COLORS.green2;
    if (l === 'initiated') return COLORS.amber;
    if (l === 'failed') return COLORS.red;
    return COLORS.earth1;
  });
  const paymentData = {
    labels: paymentLabels,
    datasets: [
      {
        label: 'Transactions',
        data: paymentValues,
        backgroundColor: paymentBarColors,
        borderRadius: 6,
        maxBarThickness: 60,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: { padding: 14, usePointStyle: true, pointStyleWidth: 10, font: { size: 12 } },
      },
    },
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { padding: 14, usePointStyle: true, pointStyleWidth: 10, font: { size: 12 } },
      },
    },
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1, font: { size: 12 } },
        grid: { color: 'rgba(0,0,0,0.06)' },
      },
      x: {
        ticks: { font: { size: 12 } },
        grid: { display: false },
      },
    },
  };

  // ---------- Progress bar helpers ----------
  const plantedCount = (plantation_status.planted || 0) + (plantation_status.growing || 0) + (plantation_status.mature || 0);
  const plantedPercent = total_sponsored > 0 ? Math.round((plantedCount / total_sponsored) * 100) : 0;
  const approvedIdeas = idea_status.approved || 0;
  const featuredIdeas = idea_status.featured || 0;
  const ideasApprovedCount = approvedIdeas + featuredIdeas;
  const ideasPercent = total_ideas > 0 ? Math.round((ideasApprovedCount / total_ideas) * 100) : 0;

  // ---------- Inline styles ----------
  const styles = {
    welcomeBanner: {
      background: 'linear-gradient(135deg, #2E7D32 0%, #66BB6A 50%, #A5D6A7 100%)',
      borderRadius: 16,
      padding: '28px 32px',
      marginBottom: 28,
      color: '#fff',
      position: 'relative',
      overflow: 'hidden',
    },
    welcomeTitle: {
      fontSize: 26,
      fontWeight: 700,
      margin: '0 0 6px 0',
    },
    welcomeSub: {
      fontSize: 15,
      opacity: 0.92,
      margin: 0,
    },
    welcomeEmoji: {
      position: 'absolute',
      right: 32,
      top: '50%',
      transform: 'translateY(-50%)',
      fontSize: 56,
      opacity: 0.25,
    },
    kpiGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: 20,
      marginBottom: 28,
    },
    kpiCard: (accentColor) => ({
      background: '#fff',
      borderRadius: 14,
      padding: '22px 24px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      borderLeft: `5px solid ${accentColor}`,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    }),
    kpiValue: {
      fontSize: 30,
      fontWeight: 700,
      color: '#1B5E20',
      lineHeight: 1.2,
    },
    kpiLabel: {
      fontSize: 13,
      color: '#666',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    kpiIcon: {
      fontSize: 22,
      marginBottom: 4,
    },
    chartsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: 24,
      marginBottom: 28,
    },
    chartCard: {
      background: '#fff',
      borderRadius: 14,
      padding: '20px 24px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    },
    chartTitle: {
      fontSize: 15,
      fontWeight: 600,
      color: '#333',
      marginBottom: 16,
      textAlign: 'center',
    },
    chartContainer: {
      position: 'relative',
      height: 240,
    },
    progressSection: {
      background: '#fff',
      borderRadius: 14,
      padding: '24px 28px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      marginBottom: 28,
    },
    progressTitle: {
      fontSize: 16,
      fontWeight: 600,
      color: '#333',
      marginBottom: 20,
    },
    progressRow: {
      marginBottom: 18,
    },
    progressLabel: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: 6,
      fontSize: 13,
      color: '#555',
    },
    progressBarOuter: {
      height: 14,
      backgroundColor: '#E8F5E9',
      borderRadius: 7,
      overflow: 'hidden',
    },
    progressBarInner: (percent, color) => ({
      height: '100%',
      width: `${Math.min(percent, 100)}%`,
      backgroundColor: color,
      borderRadius: 7,
      transition: 'width 0.6s ease',
    }),
    quickActions: {
      background: '#fff',
      borderRadius: 14,
      padding: '24px 28px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    },
    quickActionsTitle: {
      fontSize: 16,
      fontWeight: 600,
      color: '#333',
      marginBottom: 16,
    },
    quickActionsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: 14,
    },
    quickActionBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '14px 18px',
      borderRadius: 10,
      border: '1.5px solid #C8E6C9',
      background: '#F1F8E9',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textDecoration: 'none',
      color: '#2E7D32',
      fontWeight: 600,
      fontSize: 14,
    },
    quickActionIcon: {
      fontSize: 20,
    },
  };

  return (
    <div className="dashboard-overview">
      {/* Welcome Banner */}
      <div style={styles.welcomeBanner}>
        <h2 style={styles.welcomeTitle}>Welcome back! 🌿</h2>
        <p style={styles.welcomeSub}>
          Track your green impact, monitor your sponsored trees, and stay connected with the community.
        </p>
        <span style={styles.welcomeEmoji}>{'\uD83C\uDF33'}</span>
      </div>

      {/* KPI Cards */}
      <div style={styles.kpiGrid}>
        <div style={styles.kpiCard(COLORS.green1)}>
          <span style={styles.kpiIcon}>{'\uD83C\uDF33'}</span>
          <span style={styles.kpiValue}>{total_sponsored}</span>
          <span style={styles.kpiLabel}>Trees Sponsored</span>
        </div>
        <div style={styles.kpiCard(COLORS.amber)}>
          <span style={styles.kpiIcon}>{'\uD83D\uDCB0'}</span>
          <span style={styles.kpiValue}>{formatCurrency(total_spent)}</span>
          <span style={styles.kpiLabel}>Total Contribution</span>
        </div>
        <div style={styles.kpiCard(COLORS.teal)}>
          <span style={styles.kpiIcon}>{'\uD83E\uDD1D'}</span>
          <span style={styles.kpiValue}>{total_signups}</span>
          <span style={styles.kpiLabel}>Volunteer Signups</span>
        </div>
        <div style={styles.kpiCard(COLORS.blue)}>
          <span style={styles.kpiIcon}>{'\uD83D\uDCA1'}</span>
          <span style={styles.kpiValue}>{total_ideas}</span>
          <span style={styles.kpiLabel}>Ideas Submitted</span>
        </div>
      </div>

      {/* Monitoring & GPS Map (+ Monitoring History calendar) */}
      <MonitoringMapCard fetchSummary={individualAPI.getMonitoringSummary} title="Platform Monitoring Overview" />

      {/* Charts 2x2 Grid */}
      <div style={styles.chartsGrid}>
        {/* Doughnut: Plantation Progress */}
        <div style={styles.chartCard}>
          <div style={styles.chartTitle}>Plantation Progress</div>
          <div style={styles.chartContainer}>
            {plantationValues.length > 0 && plantationValues.some((v) => v > 0) ? (
              <Doughnut data={plantationData} options={doughnutOptions} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
                No plantation data yet
              </div>
            )}
          </div>
        </div>

        {/* Pie: Tree Species Distribution */}
        <div style={styles.chartCard}>
          <div style={styles.chartTitle}>Tree Species Distribution</div>
          <div style={styles.chartContainer}>
            {treeValues.length > 0 && treeValues.some((v) => v > 0) ? (
              <Pie data={treeData} options={pieOptions} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
                No tree data yet
              </div>
            )}
          </div>
        </div>

        {/* Doughnut: Idea Status */}
        <div style={styles.chartCard}>
          <div style={styles.chartTitle}>Idea Status</div>
          <div style={styles.chartContainer}>
            {ideaValues.length > 0 && ideaValues.some((v) => v > 0) ? (
              <Doughnut data={ideaData} options={doughnutOptions} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
                No ideas data yet
              </div>
            )}
          </div>
        </div>

        {/* Bar: Payment Status */}
        <div style={styles.chartCard}>
          <div style={styles.chartTitle}>Payment Status</div>
          <div style={styles.chartContainer}>
            {paymentValues.length > 0 && paymentValues.some((v) => v > 0) ? (
              <Bar data={paymentData} options={barOptions} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
                No payment data yet
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress Bars */}
      <div style={styles.progressSection}>
        <div style={styles.progressTitle}>Progress Tracking</div>

        <div style={styles.progressRow}>
          <div style={styles.progressLabel}>
            <span>Trees Planted vs Sponsored</span>
            <span>{plantedCount} / {total_sponsored} ({plantedPercent}%)</span>
          </div>
          <div style={styles.progressBarOuter}>
            <div style={styles.progressBarInner(plantedPercent, COLORS.green2)} />
          </div>
        </div>

        <div style={styles.progressRow}>
          <div style={styles.progressLabel}>
            <span>Ideas Approved vs Total</span>
            <span>{ideasApprovedCount} / {total_ideas} ({ideasPercent}%)</span>
          </div>
          <div style={styles.progressBarOuter}>
            <div style={styles.progressBarInner(ideasPercent, COLORS.teal)} />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={styles.quickActions}>
        <div style={styles.quickActionsTitle}>Quick Actions</div>
        <div style={styles.quickActionsGrid}>
          <Link to="/individual/sponsorships" style={styles.quickActionBtn}>
            <span style={styles.quickActionIcon}>{'\uD83C\uDF33'}</span>
            Sponsor a Tree
          </Link>
          <Link to="/individual/volunteering" style={styles.quickActionBtn}>
            <span style={styles.quickActionIcon}>{'\uD83E\uDD1D'}</span>
            Browse Volunteering
          </Link>
          <Link to="/individual/ideas" style={styles.quickActionBtn}>
            <span style={styles.quickActionIcon}>{'\uD83D\uDCA1'}</span>
            Share an Idea
          </Link>
          <Link to="/individual/adopt" style={styles.quickActionBtn}>
            <span style={styles.quickActionIcon}>{'\uD83C\uDFDE\uFE0F'}</span>
            Adopt a Tree Site
          </Link>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sponsor a Tree modal form
// ---------------------------------------------------------------------------
const INITIAL_SPONSORSHIP_FORM = {
  tree_type: '',
  location_description: '',
  city: '',
  state: '',
  dedication_type: 'personal',
  dedication_message: '',
  amount: '',
  branding_option: '',
};

function SponsorTreeModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(INITIAL_SPONSORSHIP_FORM);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.tree_type || !form.city || !form.state || !form.amount) {
      toast.warn('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      await individualAPI.createSponsorship(payload);
      toast.success('Tree sponsorship created successfully!');
      setForm(INITIAL_SPONSORSHIP_FORM);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create sponsorship');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} title="Sponsor a Tree" onClose={onClose}>
      <form onSubmit={handleSubmit} className="form">
        <div className="form-group">
          <label>Tree Type *</label>
          <input type="text" name="tree_type" value={form.tree_type} onChange={handleChange} placeholder="e.g. Neem, Banyan, Mango" required />
        </div>
        <div className="form-group">
          <label>Location Description</label>
          <input type="text" name="location_description" value={form.location_description} onChange={handleChange} placeholder="Describe preferred plantation area" />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>City *</label>
            <input type="text" name="city" value={form.city} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>State *</label>
            <input type="text" name="state" value={form.state} onChange={handleChange} required />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Dedication Type</label>
            <select name="dedication_type" value={form.dedication_type} onChange={handleChange}>
              <option value="personal">Personal</option>
              <option value="memory">In Memory</option>
              <option value="gift">Gift</option>
              <option value="celebration">Celebration</option>
            </select>
          </div>
          <div className="form-group">
            <label>Amount (INR) *</label>
            <input type="number" name="amount" value={form.amount} onChange={handleChange} min="1" step="1" required />
          </div>
        </div>
        <div className="form-group">
          <label>Dedication Message</label>
          <textarea name="dedication_message" value={form.dedication_message} onChange={handleChange} rows={3} placeholder="Optional dedication message..." />
        </div>
        <div className="form-group">
          <label>Branding Option</label>
          <input type="text" name="branding_option" value={form.branding_option} onChange={handleChange} placeholder="Optional branding preference" />
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Sponsor Tree'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Sponsorships list sub-page
// ---------------------------------------------------------------------------
function SponsorshipsList() {
  const [sponsorships, setSponsorships] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await individualAPI.getSponsorships({ page, per_page: 10 });
      setSponsorships(res.data.sponsorships || res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load sponsorships');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className="page-header">
        <h3>My Sponsorships</h3>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          Sponsor a Tree
        </button>
      </div>

      <SponsorTreeModal open={showModal} onClose={() => setShowModal(false)} onCreated={() => { setPage(1); load(); }} />

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : sponsorships.length === 0 ? (
        <div className="empty-state-container">
          <p className="empty-state">You have not sponsored any trees yet.</p>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>Sponsor Your First Tree</button>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tree Type</th>
                  <th>Location</th>
                  <th>Amount</th>
                  <th>Payment Status</th>
                  <th>Plantation Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sponsorships.map((s) => (
                  <tr key={s.id}>
                    <td>{s.tree_type || '-'}</td>
                    <td>{[s.city, s.state].filter(Boolean).join(', ') || s.location_description || '-'}</td>
                    <td>{formatCurrency(s.amount)}</td>
                    <td><StatusBadge status={s.payment_status} /></td>
                    <td><StatusBadge status={s.plantation_status || s.status} /></td>
                    <td>{formatDate(s.created_at)}</td>
                    <td>
                      <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/individual/sponsorships/${s.id}`)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sponsorship detail sub-page
// ---------------------------------------------------------------------------
function SponsorshipDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sponsorship, setSponsorship] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await individualAPI.getSponsorship(id);
        setSponsorship(res.data.sponsorship || res.data);
      } catch {
        toast.error('Failed to load sponsorship details');
        navigate('/individual/sponsorships');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id, navigate]);

  if (loading) return <div className="loading-spinner">Loading...</div>;
  if (!sponsorship) return null;

  const reports = sponsorship.reports || sponsorship.geotagged_reports || [];
  const milestones = sponsorship.milestones || [];

  return (
    <div>
      <button className="btn btn-secondary btn-sm" onClick={() => navigate('/individual/sponsorships')} style={{ marginBottom: 16 }}>
        &larr; Back to Sponsorships
      </button>

      <div className="card">
        <div className="card-header">
          <h3>Sponsorship #{sponsorship.id} &mdash; {sponsorship.tree_type || 'Tree'}</h3>
          <StatusBadge status={sponsorship.plantation_status || sponsorship.status} />
        </div>

        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-label">Tree Type</span>
            <span className="detail-value">{sponsorship.tree_type || '-'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Location</span>
            <span className="detail-value">
              {sponsorship.location_description || '-'}
              {sponsorship.city && ` | ${sponsorship.city}, ${sponsorship.state}`}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Amount</span>
            <span className="detail-value">{formatCurrency(sponsorship.amount)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Payment Status</span>
            <span className="detail-value"><StatusBadge status={sponsorship.payment_status} /></span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Dedication Type</span>
            <span className="detail-value" style={{ textTransform: 'capitalize' }}>{sponsorship.dedication_type || '-'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Dedication Message</span>
            <span className="detail-value">{sponsorship.dedication_message || '-'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Branding Option</span>
            <span className="detail-value">{sponsorship.branding_option || '-'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Date Sponsored</span>
            <span className="detail-value">{formatDate(sponsorship.created_at)}</span>
          </div>
        </div>
      </div>

      {/* Milestone progress */}
      {milestones.length > 0 && (
        <div className="card" style={{ marginTop: 24 }}>
          <div className="card-header">
            <h3>Milestone Progress</h3>
          </div>
          <div className="milestones-list">
            {milestones.map((m, idx) => (
              <div key={m.id || idx} className={`milestone-item ${m.completed ? 'completed' : ''}`}>
                <div className="milestone-marker">{m.completed ? '\u2713' : idx + 1}</div>
                <div className="milestone-info">
                  <strong>{m.title || m.name}</strong>
                  <span className="text-muted">{m.description || ''}</span>
                  {m.completed_at && <span className="text-muted">{formatDate(m.completed_at)}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Geotagged reports */}
      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-header">
          <h3>Geotagged Reports</h3>
        </div>
        {reports.length === 0 ? (
          <p className="empty-state">No reports available yet.</p>
        ) : (
          <div className="reports-grid">
            {reports.map((r) => (
              <div key={r.id} className="report-card">
                {r.photo_url && (
                  <div className="report-photo">
                    <img src={r.photo_url} alt={`Report ${r.id}`} />
                  </div>
                )}
                <div className="report-info">
                  <p><strong>{r.title || `Report #${r.id}`}</strong></p>
                  <p className="text-muted">{r.description || r.notes || ''}</p>
                  <div className="report-meta">
                    <span>{formatDate(r.report_date || r.created_at)}</span>
                    <StatusBadge status={r.status} />
                    {r.latitude && r.longitude && (
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        {r.latitude.toFixed(4)}, {r.longitude.toFixed(4)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Volunteering sub-page
// ---------------------------------------------------------------------------
function Volunteering() {
  const [tab, setTab] = useState('browse');
  const [activities, setActivities] = useState([]);
  const [mySignups, setMySignups] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [signingUp, setSigningUp] = useState(null);

  const loadActivities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await individualAPI.getVolunteering({ page, per_page: 9 });
      setActivities(res.data.activities || res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load volunteering activities');
    } finally {
      setLoading(false);
    }
  }, [page]);

  const loadMySignups = useCallback(async () => {
    try {
      const res = await individualAPI.getMySignups();
      setMySignups(res.data.signups || res.data.items || []);
    } catch {
      toast.error('Failed to load your signups');
    }
  }, []);

  useEffect(() => {
    if (tab === 'browse') {
      loadActivities();
    } else {
      loadMySignups();
    }
  }, [tab, loadActivities, loadMySignups]);

  const handleSignup = async (activityId) => {
    setSigningUp(activityId);
    try {
      await individualAPI.signupVolunteering(activityId);
      toast.success('Successfully signed up for the activity!');
      loadActivities();
      loadMySignups();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to sign up');
    } finally {
      setSigningUp(null);
    }
  };

  const signedUpIds = new Set(mySignups.map((s) => s.activity_id || s.id));

  return (
    <div>
      <div className="page-header">
        <h3>Volunteering</h3>
      </div>

      <div className="tabs">
        <button className={`tab ${tab === 'browse' ? 'active' : ''}`} onClick={() => { setTab('browse'); setPage(1); }}>
          Browse Activities
        </button>
        <button className={`tab ${tab === 'my' ? 'active' : ''}`} onClick={() => setTab('my')}>
          My Signups
        </button>
      </div>

      {tab === 'browse' && (
        <>
          {loading ? (
            <div className="loading-spinner">Loading...</div>
          ) : activities.length === 0 ? (
            <p className="empty-state">No volunteering activities available at the moment.</p>
          ) : (
            <>
              <div className="cards-grid">
                {activities.map((a) => (
                  <div key={a.id} className="activity-card">
                    <h4>{a.title}</h4>
                    <p className="text-muted">
                      {a.description?.substring(0, 150)}{a.description?.length > 150 ? '...' : ''}
                    </p>
                    <div className="activity-meta">
                      <span>{a.location || [a.city, a.state].filter(Boolean).join(', ') || '-'}</span>
                      <span>{formatDate(a.activity_date || a.date)}</span>
                    </div>
                    <div className="activity-footer">
                      <span className="spots-available">
                        {a.spots_available != null ? `${a.spots_available} spots left` : ''}
                      </span>
                      {signedUpIds.has(a.id) ? (
                        <span className="badge badge-completed">Signed Up</span>
                      ) : (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleSignup(a.id)}
                          disabled={signingUp === a.id || a.spots_available === 0}
                        >
                          {signingUp === a.id ? 'Signing Up...' : 'Sign Up'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <Pagination page={page} pages={pages} onPageChange={setPage} />
            </>
          )}
        </>
      )}

      {tab === 'my' && (
        <>
          {mySignups.length === 0 ? (
            <p className="empty-state">You haven't signed up for any activities yet.</p>
          ) : (
            <div className="cards-grid">
              {mySignups.map((s) => (
                <div key={s.id} className="activity-card">
                  <h4>{s.title || s.activity_title}</h4>
                  <p className="text-muted">
                    {(s.description || s.activity_description || '').substring(0, 150)}
                  </p>
                  <div className="activity-meta">
                    <span>{s.location || [s.city, s.state].filter(Boolean).join(', ') || '-'}</span>
                    <span>{formatDate(s.activity_date || s.date)}</span>
                  </div>
                  <div className="activity-footer">
                    <StatusBadge status={s.signup_status || s.status || 'confirmed'} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Post New Idea modal form
// ---------------------------------------------------------------------------
const INITIAL_IDEA_FORM = {
  title: '',
  description: '',
  category: '',
};

function PostIdeaModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(INITIAL_IDEA_FORM);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description) {
      toast.warn('Please provide a title and description');
      return;
    }
    setSubmitting(true);
    try {
      await individualAPI.postIdea(form);
      toast.success('Idea posted successfully!');
      setForm(INITIAL_IDEA_FORM);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to post idea');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} title="Post New Idea" onClose={onClose}>
      <form onSubmit={handleSubmit} className="form">
        <div className="form-group">
          <label>Title *</label>
          <input type="text" name="title" value={form.title} onChange={handleChange} placeholder="Give your idea a clear title" required />
        </div>
        <div className="form-group">
          <label>Description *</label>
          <textarea name="description" value={form.description} onChange={handleChange} rows={5} placeholder="Describe your idea in detail..." required />
        </div>
        <div className="form-group">
          <label>Category</label>
          <input type="text" name="category" value={form.category} onChange={handleChange} placeholder="e.g. Tree Planting, Community, Education" />
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Posting...' : 'Post Idea'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Ideas sub-page
// ---------------------------------------------------------------------------
function Ideas() {
  const [ideas, setIdeas] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await individualAPI.getMyIdeas({ page, per_page: 10 });
      setIdeas(res.data.ideas || res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load ideas');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className="page-header">
        <h3>Collaboration Ideas</h3>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          Post New Idea
        </button>
      </div>

      <PostIdeaModal open={showModal} onClose={() => setShowModal(false)} onCreated={() => { setPage(1); load(); }} />

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : ideas.length === 0 ? (
        <div className="empty-state-container">
          <p className="empty-state">You have not posted any ideas yet.</p>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>Share Your First Idea</button>
        </div>
      ) : (
        <>
          <div className="cards-grid">
            {ideas.map((idea) => (
              <div key={idea.id} className="idea-card">
                <div className="idea-card-header">
                  <h4>{idea.title}</h4>
                  <StatusBadge status={idea.status} />
                </div>
                <p className="text-muted">
                  {idea.description?.substring(0, 200)}{idea.description?.length > 200 ? '...' : ''}
                </p>
                <div className="idea-meta">
                  {idea.category && <span className="idea-category">{idea.category}</span>}
                  <span className="text-muted">{formatDate(idea.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Adopt Site modal form
// ---------------------------------------------------------------------------
function AdoptSiteModal({ open, onClose, onCreated, site }) {
  const [form, setForm] = useState({
    adoption_years: '1',
    branding_option: '',
    purpose: 'personal',
    dedication_message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.adoption_years) {
      toast.warn('Please select adoption duration');
      return;
    }
    setSubmitting(true);
    try {
      await adoptionAPI.createAdoption({
        site_id: site.id,
        adoption_years: parseInt(form.adoption_years),
        branding_option: form.branding_option || undefined,
        purpose: form.purpose,
        dedication_message: form.dedication_message || undefined,
      });
      toast.success('Site adopted successfully!');
      setForm({ adoption_years: '1', branding_option: '', purpose: 'personal', dedication_message: '' });
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create adoption');
    } finally {
      setSubmitting(false);
    }
  };

  if (!site) return null;
  const annualCost = site.annual_maintenance_cost || 0;
  const years = parseInt(form.adoption_years) || 1;
  const totalCost = annualCost * years;

  return (
    <Modal open={open} title={`Adopt: ${site.name}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="form">
        <div style={{ background: '#f0fdf4', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 14, color: '#333' }}>
            <strong>{site.name}</strong> &mdash; {site.site_type?.replace(/_/g, ' ')}
          </div>
          <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
            {[site.city, site.state].filter(Boolean).join(', ')} | {site.tree_count} trees
          </div>
          <div style={{ fontSize: 13, color: '#1a7431', fontWeight: 600, marginTop: 4 }}>
            {formatCurrency(annualCost)} / year
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Adoption Duration (Years) *</label>
            <select name="adoption_years" value={form.adoption_years} onChange={handleChange} required>
              {Array.from(
                { length: (site.max_adoption_years || 5) - (site.min_adoption_years || 1) + 1 },
                (_, i) => (site.min_adoption_years || 1) + i
              ).map((y) => (
                <option key={y} value={y}>{y} {y === 1 ? 'year' : 'years'}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Purpose</label>
            <select name="purpose" value={form.purpose} onChange={handleChange}>
              <option value="personal">Personal</option>
              <option value="csr">CSR</option>
              <option value="memorial">Memorial</option>
              <option value="corporate_branding">Corporate Branding</option>
              <option value="community">Community</option>
            </select>
          </div>
        </div>
        <div className="form-group">
          <label>Branding Option</label>
          <input type="text" name="branding_option" value={form.branding_option} onChange={handleChange}
            placeholder="e.g. Name plate, banner, signboard" />
        </div>
        <div className="form-group">
          <label>Dedication Message</label>
          <textarea name="dedication_message" value={form.dedication_message} onChange={handleChange}
            rows={3} placeholder="Optional dedication message..." />
        </div>
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 16, marginBottom: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#92400e' }}>
            Total Cost: {formatCurrency(totalCost)} for {years} {years === 1 ? 'year' : 'years'}
          </div>
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Confirming...' : 'Confirm Adoption'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Browse Adoption Sites sub-page
// ---------------------------------------------------------------------------
function BrowseAdoptionSites() {
  const [sites, setSites] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ city: '', state: '', site_type: '' });
  const [selectedSite, setSelectedSite] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 9 };
      if (filters.city) params.city = filters.city;
      if (filters.state) params.state = filters.state;
      if (filters.site_type) params.site_type = filters.site_type;
      const res = await adoptionAPI.getSites(params);
      setSites(res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load adoption sites');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const openAdopt = (site) => {
    setSelectedSite(site);
    setShowModal(true);
  };

  return (
    <div>
      <div className="page-header">
        <h3>Adopt a Tree Site</h3>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input name="city" placeholder="Filter by city..." value={filters.city}
          onChange={handleFilterChange} className="form-control" style={{ maxWidth: 200 }} />
        <input name="state" placeholder="Filter by state..." value={filters.state}
          onChange={handleFilterChange} className="form-control" style={{ maxWidth: 200 }} />
        <select name="site_type" value={filters.site_type} onChange={handleFilterChange} className="form-control" style={{ maxWidth: 200 }}>
          <option value="">All Site Types</option>
          <option value="park">Park</option>
          <option value="road_stretch">Road Stretch</option>
          <option value="tree_cluster">Tree Cluster</option>
          <option value="garden">Garden</option>
          <option value="campus">Campus</option>
        </select>
      </div>

      <AdoptSiteModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onCreated={() => { load(); }}
        site={selectedSite}
      />

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : sites.length === 0 ? (
        <p className="empty-state">No adoption sites available matching your filters.</p>
      ) : (
        <>
          <div className="cards-grid">
            {sites.map((site) => (
              <div key={site.id} className="card" style={{ padding: 20 }}>
                <h4 style={{ margin: '0 0 8px' }}>{site.name}</h4>
                <div style={{ marginBottom: 8 }}>
                  <StatusBadge status={site.site_type?.replace(/_/g, '-')} />
                </div>
                <p className="text-muted" style={{ fontSize: 14, marginBottom: 12 }}>
                  {site.description?.substring(0, 120)}{site.description?.length > 120 ? '...' : ''}
                </p>
                <div style={{ fontSize: 13, color: '#555', lineHeight: 1.8 }}>
                  <div><strong>Location:</strong> {[site.city, site.state].filter(Boolean).join(', ') || '-'}</div>
                  <div><strong>Trees:</strong> {site.tree_count || 0}</div>
                  <div><strong>Condition:</strong> <span style={{ textTransform: 'capitalize' }}>{site.current_condition?.replace(/_/g, ' ') || '-'}</span></div>
                  <div><strong>Annual Cost:</strong> {formatCurrency(site.annual_maintenance_cost)}</div>
                  <div><strong>Duration:</strong> {site.min_adoption_years || 1}-{site.max_adoption_years || 5} years</div>
                </div>
                <button className="btn btn-primary btn-sm" style={{ marginTop: 14, width: '100%' }}
                  onClick={() => openAdopt(site)}>
                  Adopt This Site
                </button>
              </div>
            ))}
          </div>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// My Adoptions list sub-page
// ---------------------------------------------------------------------------
function MyAdoptionsList() {
  const [adoptions, setAdoptions] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adoptionAPI.getMyAdoptions({ page, per_page: 10 });
      setAdoptions(res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load adoptions');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="page-header">
        <h3>My Adoptions</h3>
        <Link to="/individual/adopt" className="btn btn-primary">Adopt a Site</Link>
      </div>

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : adoptions.length === 0 ? (
        <div className="empty-state-container">
          <p className="empty-state">You have not adopted any tree sites yet.</p>
          <Link to="/individual/adopt" className="btn btn-primary">Browse Sites to Adopt</Link>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Site Name</th>
                  <th>Location</th>
                  <th>Years</th>
                  <th>Total Amount</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Start Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {adoptions.map((a) => (
                  <tr key={a.id}>
                    <td>{a.site_name || '-'}</td>
                    <td>{[a.city, a.state].filter(Boolean).join(', ') || '-'}</td>
                    <td>{a.adoption_years}</td>
                    <td>{formatCurrency(a.total_amount)}</td>
                    <td><StatusBadge status={a.payment_status} /></td>
                    <td><StatusBadge status={a.adoption_status} /></td>
                    <td>{formatDate(a.start_date)}</td>
                    <td>
                      <button className="btn btn-secondary btn-sm"
                        onClick={() => navigate(`/individual/my-adoptions/${a.id}`)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Adoption detail sub-page
// ---------------------------------------------------------------------------
function AdoptionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [adoption, setAdoption] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await adoptionAPI.getAdoption(id);
        setAdoption(res.data.adoption || res.data);
      } catch {
        toast.error('Failed to load adoption details');
        navigate('/individual/my-adoptions');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id, navigate]);

  if (loading) return <div className="loading-spinner">Loading...</div>;
  if (!adoption) return null;

  const reports = adoption.reports || [];
  const site = adoption.site || {};

  return (
    <div>
      <button className="btn btn-secondary btn-sm" onClick={() => navigate('/individual/my-adoptions')} style={{ marginBottom: 16 }}>
        &larr; Back to Adoptions
      </button>

      <div className="card">
        <div className="card-header">
          <h3>Adoption #{adoption.id} &mdash; {adoption.site_name || site.name || 'Site'}</h3>
          <StatusBadge status={adoption.adoption_status} />
        </div>

        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-label">Site Name</span>
            <span className="detail-value">{adoption.site_name || site.name || '-'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Site Type</span>
            <span className="detail-value" style={{ textTransform: 'capitalize' }}>
              {(adoption.site_type || site.site_type || '-').replace(/_/g, ' ')}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Location</span>
            <span className="detail-value">
              {[adoption.city || site.city, adoption.state || site.state].filter(Boolean).join(', ') || '-'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Trees</span>
            <span className="detail-value">{adoption.tree_count || site.tree_count || 0}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Duration</span>
            <span className="detail-value">{adoption.adoption_years} year(s)</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Period</span>
            <span className="detail-value">{formatDate(adoption.start_date)} to {formatDate(adoption.end_date)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Annual Amount</span>
            <span className="detail-value">{formatCurrency(adoption.annual_amount)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Total Amount</span>
            <span className="detail-value">{formatCurrency(adoption.total_amount)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Payment Status</span>
            <span className="detail-value"><StatusBadge status={adoption.payment_status} /></span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Purpose</span>
            <span className="detail-value" style={{ textTransform: 'capitalize' }}>
              {(adoption.purpose || '-').replace(/_/g, ' ')}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Branding</span>
            <span className="detail-value">{adoption.branding_option || '-'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Dedication</span>
            <span className="detail-value">{adoption.dedication_message || '-'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Next Report Due</span>
            <span className="detail-value">{formatDate(adoption.next_report_due)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Reports Received</span>
            <span className="detail-value">{adoption.reports_received || 0}</span>
          </div>
        </div>
      </div>

      {/* Site details card */}
      {site.name && (
        <div className="card" style={{ marginTop: 24 }}>
          <div className="card-header"><h3>Site Details</h3></div>
          <p>{site.description || 'No description available.'}</p>
          {site.amenities && <p style={{ marginTop: 8 }}><strong>Amenities:</strong> {site.amenities}</p>}
          {site.address && <p style={{ marginTop: 8 }}><strong>Address:</strong> {site.address}</p>}
        </div>
      )}

      {/* Adoption Reports */}
      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-header"><h3>Adoption Reports</h3></div>
        {reports.length === 0 ? (
          <p className="empty-state">No adoption reports available yet.</p>
        ) : (
          <div className="reports-grid">
            {reports.map((r) => (
              <div key={r.id} className="report-card">
                <div className="report-info">
                  <p><strong>{r.report_quarter} &mdash; {(r.report_type || '').replace(/_/g, ' ')}</strong></p>
                  <p className="text-muted">{r.description || r.maintenance_activities || ''}</p>
                  <div className="report-meta">
                    <span>{formatDate(r.submitted_at)}</span>
                    <StatusBadge status={r.status} />
                    <span>Health: <span style={{ textTransform: 'capitalize' }}>{r.tree_health_status || '-'}</span></span>
                  </div>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    Healthy: {r.trees_healthy || 0} | Attention: {r.trees_needing_attention || 0} | Replaced: {r.trees_replaced || 0}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Blog Categories constant
// ---------------------------------------------------------------------------
const BLOG_CATEGORIES = ['Tree Knowledge', 'Heritage', 'CSR Impact', 'Technology', 'Environment', 'Community', 'Green Offerings'];

const INITIAL_BLOG_FORM = { title: '', summary: '', content: '', category: '', tags: '', read_time_min: 5, featured_image: '' };

// ---------------------------------------------------------------------------
// My Blog Posts (reusable across dashboards)
// ---------------------------------------------------------------------------
function MyBlogPosts({ api }) {
  const [blogs, setBlogs] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [form, setForm] = useState(INITIAL_BLOG_FORM);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 10 };
      if (statusFilter) params.status = statusFilter;
      const res = await api.getMyBlogs(params);
      setBlogs(res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, api]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditPost(null);
    setForm(INITIAL_BLOG_FORM);
    setShowModal(true);
  };

  const openEdit = (post) => {
    setEditPost(post);
    setForm({
      title: post.title || '',
      summary: post.summary || '',
      content: post.content || '',
      category: post.category || '',
      tags: post.tags || '',
      read_time_min: post.read_time_min || 5,
      featured_image: post.featured_image || '',
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: name === 'read_time_min' ? parseInt(value) || 0 : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content) {
      toast.warn('Title and content are required');
      return;
    }
    setSubmitting(true);
    try {
      if (editPost) {
        await api.updateBlog(editPost.id, form);
        toast.success('Blog post updated successfully!');
      } else {
        await api.createBlog(form);
        toast.success('Blog post created successfully!');
      }
      setShowModal(false);
      setEditPost(null);
      setForm(INITIAL_BLOG_FORM);
      setPage(1);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save blog post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await api.deleteBlog(id);
      toast.success('Blog post deleted');
      load();
    } catch {
      toast.error('Failed to delete blog post');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h3>My Blog Posts</h3>
        <button className="btn btn-primary" onClick={openCreate}>+ Create Blog Post</button>
      </div>

      {/* Status filter buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[
          { value: '', label: 'All' },
          { value: 'pending_review', label: 'Pending Review' },
          { value: 'published', label: 'Published' },
          { value: 'rejected', label: 'Rejected' },
        ].map(s => (
          <button key={s.value} onClick={() => { setStatusFilter(s.value); setPage(1); }}
            className={`btn btn-sm ${statusFilter === s.value ? 'btn-primary' : 'btn-secondary'}`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Blog editor modal */}
      <Modal open={showModal} title={editPost ? 'Edit Blog Post' : 'Create Blog Post'} onClose={() => { setShowModal(false); setEditPost(null); }}>
        <form onSubmit={handleSubmit} className="form">
          <div className="form-group">
            <label>Title *</label>
            <input type="text" name="title" value={form.title} onChange={handleChange} placeholder="Blog post title" required />
          </div>
          <div className="form-group">
            <label>Summary</label>
            <textarea name="summary" value={form.summary} onChange={handleChange} rows={2} placeholder="Brief summary of your post" />
          </div>
          <div className="form-group">
            <label>Content *</label>
            <textarea name="content" value={form.content} onChange={handleChange} rows={8} placeholder="Write your blog post content..." required />
          </div>
          <div className="form-group">
            <label>Category</label>
            <select name="category" value={form.category} onChange={handleChange}>
              <option value="">Select a category</option>
              {BLOG_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Tags (comma-separated)</label>
            <input type="text" name="tags" value={form.tags} onChange={handleChange} placeholder="e.g. trees, sustainability, green" />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Read Time (minutes)</label>
              <input type="number" name="read_time_min" value={form.read_time_min} onChange={handleChange} min={1} max={60} />
            </div>
            <div className="form-group" style={{ flex: 2 }}>
              <label>Featured Image URL</label>
              <input type="text" name="featured_image" value={form.featured_image} onChange={handleChange} placeholder="https://example.com/image.jpg" />
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); setEditPost(null); }} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : editPost ? 'Update Post' : 'Create Post'}
            </button>
          </div>
        </form>
      </Modal>

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : blogs.length === 0 ? (
        <div className="empty-state-container">
          <p className="empty-state">No blog posts found.</p>
          <button className="btn btn-primary" onClick={openCreate}>Create Your First Blog Post</button>
        </div>
      ) : (
        <>
          <div className="card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {blogs.map(post => (
                  <tr key={post.id}>
                    <td>
                      <strong>{post.title}</strong>
                      {post.status === 'rejected' && post.rejection_reason && (
                        <div style={{ fontSize: '12px', color: '#b45309', marginTop: '4px' }}>
                          Rejection reason: {post.rejection_reason}
                        </div>
                      )}
                    </td>
                    <td>{post.category || '-'}</td>
                    <td><StatusBadge status={post.status} /></td>
                    <td>{formatDate(post.created_at)}</td>
                    <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {post.status !== 'published' && (
                        <button className="btn btn-sm btn-secondary" onClick={() => openEdit(post)}>Edit</button>
                      )}
                      {post.status !== 'published' && (
                        <button className="btn btn-sm" style={{ background: '#ef5350', color: '#fff' }} onClick={() => handleDelete(post.id)}>Delete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main IndividualDashboard component
// ---------------------------------------------------------------------------
export default function IndividualDashboard() {
  return (
    <DashboardLayout navItems={NAV_ITEMS} title="Individual Dashboard">
      <Routes>
        <Route index element={<Overview />} />
        <Route path="sponsorships" element={<SponsorshipsList />} />
        <Route path="sponsorships/:id" element={<SponsorshipDetail />} />
        <Route path="adopt" element={<BrowseAdoptionSites />} />
        <Route path="my-adoptions" element={<MyAdoptionsList />} />
        <Route path="my-adoptions/:id" element={<AdoptionDetail />} />
        <Route path="volunteering" element={<Volunteering />} />
        <Route path="ideas" element={<Ideas />} />
        <Route path="blog" element={<MyBlogPosts api={individualAPI} />} />
        <Route path="species-report" element={<SpeciesReport />} />
      </Routes>
    </DashboardLayout>
  );
}
